"use client";

import React, { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";

// components
const AnnouncementBanner = dynamic(() => import("@/components/AnnouncementBanner"), { ssr: false });
const WelcomeModal = dynamic(() => import("@/components/Modals/WelcomeModal"), { ssr: false });
import HirerHeader from "@/components/Header/HirerHeader";
import { LockoutModalContextProvider } from "@/contexts/LockoutModalContext";
import { withCompletedStepContext } from "@/contexts/CompletedStepModalContext";
import { withHirerModalsContext } from "@/contexts/HirerModalsContext";
import AppFooter from "@/components/Footer/AppFooter";
import HirerMobileBanner from "@/components/HirerMobileBanner";
import GettingStarted from "@/containers/GettingStarted"; // required to not use dynamic import to increase CLS

// helpers
import account from "@/api/query/account";
import analytics from "@/api/analytics";
import useUserProfile from "@/hooks/useUserProfile";
import dayjs from "@/helpers/dayjs";

// assets
import styles from "@/assets/styles/layouts/hirer-layout.module.scss";


const HirerLayout = (props: { children: React.ReactNode, isAuthorized?: boolean }) => {
  const { children, isAuthorized: initIsAuthorized } = props;

  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);

  const { data: user, isAuthorized } = useUserProfile({ initIsAuthorized });
  const pathname = usePathname();
  account.useCheckUserDeactivated();

  // effects
  useEffect(() => {
    const hirerVisitSync = localStorage.getItem("hirerVisitSync") as string;
    const days = dayjs().diff(hirerVisitSync, "days");

    if ((!hirerVisitSync || days) && user && user.type === "hirer") {
      analytics.pageView().then(() => localStorage.setItem("hirerVisitSync", `${new Date()}`));
    }

    if (user && user.type === "hirer") {
      (window as unknown as Record<string, unknown>).appziSettings = {
        "userId": user.id,
        data: {
          "userCreatedAt": user.dateJoined,
        },
      };
    }
  }, [pathname]);

  useEffect(() => {
    if (user && user.type === "hirer" && user.isBespokeModalShown && process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL") {
      if (document.readyState === "complete") {
        setTimeout(() => {
          setIsWelcomeModalOpen(true);
        }, 5000);
      }
    }
  }, [user, pathname]);

  return (
    <div className={styles["locl-hirer-layout"]} data-testid="locl-hirer-layout">
      <LockoutModalContextProvider>
        <div className={styles["locl-hirer-layout__fixed-header"]}>
          <AnnouncementBanner/>
          <HirerHeader isAuthorized={initIsAuthorized}/>
        </div>
        <div className={styles["locl-hirer-layout__page-content"]}>
          { (user?.shouldProgressBarBeVisible || !isAuthorized) && <GettingStarted/> }
          <HirerMobileBanner />
          { children }
        </div>
        <AppFooter />
        <WelcomeModal open={isWelcomeModalOpen} onCancel={() => setIsWelcomeModalOpen(false)} />
      </LockoutModalContextProvider>
    </div>
  );
};

export default withHirerModalsContext(withCompletedStepContext(HirerLayout));
